/* ════════════════════════════════════════════════════════════════════════
   RECIPE EXTRACTION UTILS — parse free-form recipe text into recipe fields.
   Pure helpers; no API calls. A future AI/vision integration can replace
   `extractRecipeFromImageWithAI` without touching call sites.
   ════════════════════════════════════════════════════════════════════════ */

const CATEGORY_HINTS = [
  ["Breakfast", /breakfast|porridge|granola|pancake|smoothie/i],
  ["Lunch",     /lunch|wrap|sandwich|salad/i],
  ["Dinner",    /dinner|roast|curry|stew|pasta|stir.?fry|risotto/i],
  ["Baking",    /cake|cookie|biscuit|muffin|scone|loaf|bread/i],
  ["Dessert",   /dessert|pudding|crumble|tart|cheesecake/i],
];

const HEADING_RE = {
  ingredients: /^(ingredients|you('|’)?ll need)[:\s]*$/i,
  instructions: /^(method|directions|instructions|steps|how to make)[:\s]*$/i,
  notes: /^(notes?|tips?)[:\s]*$/i,
};

/* Clean an ingredient list — trim, drop empties / bullets, max 80 chars. */
export function cleanIngredientLines(lines = []) {
  return (lines || [])
    .map(l => String(l || "").replace(/^[•·\-*–—\d.)\s]+/, "").trim())
    .filter(Boolean)
    .map(l => l.slice(0, 160));
}

/* Clean instruction steps — strip leading numbering, drop empties. */
export function cleanInstructionSteps(lines = []) {
  return (lines || [])
    .map(l => String(l || "").replace(/^(\d+[.)]\s*|step\s*\d+[:.\s]*)/i, "").trim())
    .filter(Boolean);
}

function detectCategory(text) {
  const hit = CATEGORY_HINTS.find(([, re]) => re.test(text || ""));
  return hit ? hit[0] : "";
}

function pickNumberAfter(label, text) {
  const re = new RegExp(`${label}[\\s:]*([0-9]+(?:\\.[0-9]+)?)`, "i");
  const m  = (text || "").match(re);
  return m ? Number(m[1]) : null;
}

/* ── Main local parser ───────────────────────────────────────────────
   Heuristic: split into lines, look for the Ingredients / Method
   headings, group everything between them. Pulls servings and times
   from the first 20 lines. */
export function extractRecipeFromTextLocal(rawText = "") {
  const text = String(rawText || "").replace(/\r\n/g, "\n").trim();
  if (!text) return buildEmptyExtraction();

  const lines = text.split("\n").map(l => l.trim());
  const nonEmpty = lines.filter(Boolean);
  const head = nonEmpty.slice(0, 20).join(" ");

  let section = null;       // "ingredients" | "instructions" | "notes"
  const ingredients = [];
  const instructions = [];
  const notes = [];
  const needsReview = [];

  lines.forEach((line) => {
    if (!line) return;
    if (HEADING_RE.ingredients.test(line))  { section = "ingredients";  return; }
    if (HEADING_RE.instructions.test(line)) { section = "instructions"; return; }
    if (HEADING_RE.notes.test(line))        { section = "notes";        return; }
    if (section === "ingredients")  ingredients.push(line);
    else if (section === "instructions") instructions.push(line);
    else if (section === "notes") notes.push(line);
  });

  // If no explicit sections were detected, guess: bulleted/short lines = ingredients,
  // long lines = instructions.
  if (ingredients.length === 0 && instructions.length === 0) {
    nonEmpty.slice(1).forEach(l => {
      const looksIngredient = /^[•\-*]|^\d+(?:[./]\d+)?\s|\b(cup|tbsp|tsp|g|kg|ml|l|slice|clove|egg|can|packet)s?\b/i.test(l) || l.length <= 50;
      if (looksIngredient) ingredients.push(l);
      else instructions.push(l);
    });
    needsReview.push("Auto-split ingredients vs method — please verify.");
  }

  const cleanedIngredients  = cleanIngredientLines(ingredients);
  const cleanedInstructions = cleanInstructionSteps(instructions);

  const title    = nonEmpty[0] || "Untitled recipe";
  const servings = pickNumberAfter("(?:serves|makes|yield)", head);
  const prep     = pickNumberAfter("(?:prep(?:aration)?)", head);
  const cook     = pickNumberAfter("(?:cook)", head);

  // Confidence: based on how many sections we recovered.
  const recovered = [
    cleanedIngredients.length > 0,
    cleanedInstructions.length > 0,
    !!title,
  ].filter(Boolean).length;
  const confidence = recovered === 3 ? "Good" : recovered === 2 ? "Medium" : "Low";
  if (cleanedInstructions.length === 0) needsReview.push("No method steps detected.");
  if (cleanedIngredients.length === 0)  needsReview.push("No ingredient lines detected.");

  return {
    title,
    subtitle: "",
    category: detectCategory(text),
    servings: servings ?? "",
    prepTime: prep ?? "",
    cookTime: cook ?? "",
    ingredients: cleanedIngredients,
    instructions: cleanedInstructions,
    notes: notes.join("\n"),
    tags: [],
    nutrition: { caloriesPerServe: null, protein: null, carbs: null, fat: null },
    confidence,
    assumptions: [],
    needsReview,
  };
}

/* Validate an extraction result and surface anything missing. */
export function validateExtractedRecipe(extr) {
  if (!extr) return { ok: false, issues: ["Empty extraction"] };
  const issues = [];
  if (!extr.title || extr.title.length < 2) issues.push("Missing title");
  if (!extr.ingredients || extr.ingredients.length === 0) issues.push("No ingredients");
  if (!extr.instructions || extr.instructions.length === 0) issues.push("No method");
  return { ok: issues.length === 0, issues };
}

/* Build a RecipeEditModal-shaped draft from an extraction (no auto-save). */
export function buildRecipeDraftFromExtraction(extraction, originalImageUrl = "") {
  if (!extraction) return null;
  return {
    title: extraction.title || "",
    subtitle: extraction.subtitle || "",
    category: extraction.category || "Dinner",
    serves: Number(extraction.servings) || "",
    prepTime: Number(extraction.prepTime) || "",
    cookTime: Number(extraction.cookTime) || "",
    ingredients: extraction.ingredients?.length ? extraction.ingredients : [""],
    instructions: extraction.instructions?.length ? extraction.instructions : [""],
    notes: extraction.notes || "",
    tags: extraction.tags || [],
    imageUrl: originalImageUrl || "",
    caloriesPerServe: extraction.nutrition?.caloriesPerServe ?? null,
    macros: {
      protein: extraction.nutrition?.protein ?? null,
      carbs:   extraction.nutrition?.carbs   ?? null,
      fat:     extraction.nutrition?.fat     ?? null,
    },
    nutritionEstimateSource: extraction.nutrition?.caloriesPerServe != null ? "extracted-from-image" : null,
    nutritionConfidence: extraction.confidence || "Low",
    nutritionNotes: (extraction.needsReview || []).join(" "),
  };
}

export function parseRecipeTextToFields(text) {
  return extractRecipeFromTextLocal(text);
}

/* Placeholder for future AI/vision/OCR integration. Until a secure backend
   helper exists, calling this returns a "needs manual paste" signal so the
   UI can fall back gracefully. NEVER calls a private API from the browser. */
// eslint-disable-next-line no-unused-vars
export async function extractRecipeFromImageWithAI(_imageDataUrlOrBlob) {
  return {
    ok: false,
    reason: "ai-vision-not-connected",
    extraction: null,
    message: "AI recipe extraction requires a secure vision/OCR connection. For now, upload the image and paste/edit the recipe text below.",
  };
}

function buildEmptyExtraction() {
  return {
    title: "", subtitle: "", category: "",
    servings: "", prepTime: "", cookTime: "",
    ingredients: [], instructions: [], notes: "",
    tags: [], nutrition: { caloriesPerServe: null, protein: null, carbs: null, fat: null },
    confidence: "Low", assumptions: [], needsReview: ["No text provided"],
  };
}
